import React, { useState } from 'react';
import Layout from '../components/Layout';
import PageTabBar from '../components/PageTabBar';
import { useAuth } from '../contexts/AuthContext';
import { Upload, FileText, Check, AlertTriangle, Loader2, Eye, Database } from 'lucide-react';

const DATABASE_TABS = [
  { label: 'Vue & Backups', to: '/admin/database', exact: true, icon: <Database className="w-4 h-4" /> },
  { label: 'Import de données', to: '/admin/import-data', exact: true, icon: <Upload className="w-4 h-4" /> },
];

const COLLECTIONS = [
  { id: 'clients', label: 'Clients', desc: 'Nom, prénom, téléphone, email, adresse' },
  { id: 'stock', label: 'Stock / Pièces', desc: 'Référence, nom, quantité, prix' },
  { id: 'devices', label: 'Réparations', desc: 'Référence, marque, modèle, problème' },
];

const DataImportPage = () => {
  const { api } = useAuth();
  const [collection, setCollection] = useState('clients');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handlePreview = async () => {
    if (!file) return;
    setError(''); setPreviewing(true); setPreview(null); setResult(null);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('collection', collection);
    try {
      const res = await api.post('/import/preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur de lecture du fichier');
    } finally { setPreviewing(false); }
  };

  const handleImport = async () => {
    if (!file) return;
    setError(''); setImporting(true); setResult(null);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('collection', collection);
    try {
      const res = await api.post('/import/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(res.data);
      setPreview(null);
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur d'import");
    } finally { setImporting(false); }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto fade-in" data-testid="data-import-page">
        <PageTabBar tabs={DATABASE_TABS} activeColor="amber" />
        <div className="flex items-center gap-3 mb-6">
          <Database className="w-5 h-5 text-[#009b3a]" />
          <h1 className="text-xl font-bold text-white">Import de données</h1>
        </div>

        {/* Collection choice */}
        <div className="mb-5">
          <p className="text-xs text-gray-400 mb-2">Type de données à importer :</p>
          <div className="grid grid-cols-3 gap-3">
            {COLLECTIONS.map(c => (
              <button key={c.id} onClick={() => { setCollection(c.id); setPreview(null); setResult(null); }}
                data-testid={`import-col-${c.id}`}
                className={`p-3 rounded-xl border text-left transition-all ${collection === c.id ? 'border-[#009b3a] bg-[#009b3a]/10' : 'border-white/10 bg-white/5 hover:bg-white/10'}`}>
                <div className="text-sm font-bold text-white">{c.label}</div>
                <div className="text-[10px] text-gray-500">{c.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* File upload */}
        <div className="mb-5">
          <label className="block p-6 rounded-xl border-2 border-dashed border-white/10 hover:border-white/20 text-center cursor-pointer transition-all" data-testid="import-file-zone">
            <Upload className="w-8 h-8 mx-auto mb-2 text-gray-500" />
            <p className="text-sm text-gray-400">{file ? file.name : 'Choisir un fichier CSV, SQL ou JSON'}</p>
            <p className="text-[10px] text-gray-600 mt-1">Max 10 Mo — 5000 lignes</p>
            <input type="file" accept=".csv,.sql,.json,.txt" onChange={e => { setFile(e.target.files?.[0] || null); setPreview(null); setResult(null); }} className="hidden" />
          </label>
        </div>

        {/* Actions */}
        {file && (
          <div className="flex gap-3 mb-5">
            <button onClick={handlePreview} disabled={previewing} data-testid="preview-import-btn"
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border border-white/10 text-white hover:bg-white/5 disabled:opacity-50">
              {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />} Aperçu
            </button>
            <button onClick={handleImport} disabled={importing} data-testid="confirm-import-btn"
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-[#050a14] bg-gradient-to-r from-[#009b3a] to-[#fedd00] hover:opacity-90 disabled:opacity-50">
              {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} Importer
            </button>
          </div>
        )}

        {error && <div className="mb-5 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-400" data-testid="import-error"><AlertTriangle className="w-3.5 h-3.5 inline mr-1" />{error}</div>}

        {/* Preview */}
        {preview && (
          <div className="mb-5 p-4 rounded-xl bg-white/5 border border-white/10" data-testid="import-preview">
            <h3 className="text-sm font-semibold text-white mb-2">Aperçu ({preview.total_rows} lignes détectées)</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr>{preview.columns.map(c => <th key={c} className="text-left px-2 py-1.5 text-gray-500 border-b border-white/5">{c}</th>)}</tr></thead>
                <tbody>{preview.preview.map((row, i) => (
                  <tr key={i} className="border-b border-white/5">{preview.columns.map(c => <td key={c} className="px-2 py-1.5 text-gray-300 max-w-[150px] truncate">{row[c] || ''}</td>)}</tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/20" data-testid="import-result">
            <div className="flex items-center gap-2 mb-2"><Check className="w-5 h-5 text-green-400" /><span className="text-sm font-semibold text-green-300">Import réussi</span></div>
            <p className="text-xs text-green-400/70">{result.imported} document(s) importé(s) dans "{result.collection}"</p>
          </div>
        )}

        {/* Help */}
        <div className="mt-6 p-4 rounded-xl bg-white/5 border border-white/10">
          <h3 className="text-xs font-semibold text-gray-400 mb-2">Formats supportés</h3>
          <ul className="text-xs text-gray-500 space-y-1">
            <li><strong>CSV</strong> — Colonnes séparées par virgules avec en-tête</li>
            <li><strong>SQL</strong> — Instructions INSERT INTO (dump MySQL/PostgreSQL)</li>
            <li><strong>JSON</strong> — Tableau d'objets [{"{"}...{"}"}, ...]</li>
          </ul>
        </div>
      </div>
    </Layout>
  );
};

export default DataImportPage;
